import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * An icon-topped tile wrapping one input — the editable sibling of
 * StageSummaryView.jsx's read-only `StatTile`. Same visual language (icon,
 * uppercase label, bordered tile) so a Vitals form and its own read-only
 * "what was recorded" view agree on what a vital sign looks like; this one
 * takes the input as `children` instead of a fixed value, since a form field
 * needs to stay a real `<input>`/`<select>` for focus, typing and voice-fill
 * to keep working, not a static display string.
 */
export interface StatFieldProps {
  icon: string;
  label: string;
  children: ReactNode;
  /** Amber tile treatment for a reading outside the expected range — same
   * intent as StatTile's `warn` prop, kept advisory-only (never blocks save). */
  warn?: boolean;
  className?: string;
}

export function StatField({ icon, label, children, warn, className }: StatFieldProps) {
  return (
    <div
      className={cn(
        'flex min-w-[128px] flex-1 flex-col gap-1.5 rounded-[var(--radius)] border p-2.5',
        warn ? 'border-warn/50 bg-warn-soft' : 'border-line bg-canvas/60',
        className
      )}
    >
      <div className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-wide text-ink-muted">
        <span className="text-sm leading-none">{icon}</span>
        {label}
      </div>
      {children}
    </div>
  );
}
